import React, { useCallback, useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";
import { FaFolderPlus } from "react-icons/fa6";
import { FcFolder, FcOpenedFolder } from "react-icons/fc";
import { MdDriveFileMoveOutline } from "react-icons/md";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner, applyClassToCharacters } from "../../constants/const";
import { GetLanguageString } from "../helper/Components";
import { ApiDocuments, ApiFolders } from "./ApiDocuments";
import AddNewFolder from "./AddNewFolder";
import BreadcrumbNavigation from "./subcomponents/BreadcrumbNavigation";

const MoveItemsModal = ({
  selectedItems,
  currentFolderId,
  folderUnitId,
  onClose,
  onMove,
  moveInProgress,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [browseFolderId, setBrowseFolderId] = useState(currentFolderId || 0);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [newFolderModal, setNewFolderModal] = useState(false);

  const movedFolderIds = useMemo(
    () =>
      new Set(
        (selectedItems || [])
          .filter((item) => (item.fileType || "").includes("folder"))
          .map((item) => item.id)
      ),
    [selectedItems]
  );

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  const loadFolders = useCallback(
    (folderId) => {
      setLoading(true);
      Promise.all([
        ApiDocuments().getDocuments({
          folderUnitId: folderUnitId || 0,
          folderId: folderId || 0,
          searchString: "",
          tags: [],
        }),
        ApiFolders().getFolderHierarchy(folderUnitId || 0, folderId || 0),
      ])
        .then(([docsRes, hierarchyRes]) => {
          const files = docsRes.data?.files || [];
          const folderList = files.filter(
            (file) =>
              (file.fileType || "").includes("folder") &&
              file.folderUnitId === null
          );
          setFolders(folderList);
          setBrowseFolderId(docsRes.data?.folderId ?? folderId);
          setBreadcrumbs(hierarchyRes.data?.hierarchy || []);
        })
        .catch((err) => {
          console.error(err);
          swal({
            title: err.message || "Error occurred",
            icon: "error",
            buttons: "OK",
          });
        })
        .finally(() => setLoading(false));
    },
    [folderUnitId]
  );

  useEffect(() => {
    loadFolders(currentFolderId || 0);
  }, [currentFolderId, loadFolders]);

  const openFolder = (folder) => {
    if (movedFolderIds.has(folder.id) || moveInProgress) return;
    loadFolders(folder.id);
  };

  const handleBreadcrumbClick = (item) => {
    if (movedFolderIds.has(item.folderId) || moveInProgress) return;
    loadFolders(item.folderId);
  };

  const refreshPicker = () => {
    loadFolders(browseFolderId);
  };

  const isInvalidDestination =
    !browseFolderId ||
    browseFolderId === 0 ||
    movedFolderIds.has(browseFolderId);

  return (
    <>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            <MdDriveFileMoveOutline size={22} style={{ marginTop: -4 }} />
            &nbsp;
            <GetLanguageString props="document_select_destination" />
          </ModalHeader>
          <ModalBody className="no-drag">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <BreadcrumbNavigation
                  foldersBreadCrumbs={breadcrumbs}
                  onBreadcrumbClick={handleBreadcrumbClick}
                  hierarchyDisable={loading || moveInProgress}
                  deletionInProgress={false}
                />
              </div>
              <Button
                color="primary"
                size="sm"
                style={{ borderRadius: 16, whiteSpace: "nowrap" }}
                disabled={loading || moveInProgress || !browseFolderId}
                onClick={() => setNewFolderModal(true)}
              >
                <FaFolderPlus size={14} />
                &nbsp;
                <GetLanguageString props="document_new_folder" />
              </Button>
            </div>

            {loading ? (
              LoadingSpinner()
            ) : folders.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
                <GetLanguageString props="document_no_file_folder_present_only" />
              </div>
            ) : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                <Table hover responsive className="table-sm">
                  <tbody>
                    {folders.map((folder) => {
                      const isMovedFolder = movedFolderIds.has(folder.id);
                      return (
                        <tr
                          key={folder.id}
                          style={{
                            cursor: isMovedFolder ? "not-allowed" : "pointer",
                            opacity: isMovedFolder ? 0.45 : 1,
                          }}
                          onDoubleClick={() => openFolder(folder)}
                          onClick={() => openFolder(folder)}
                        >
                          <td style={{ width: 36 }}>
                            {isMovedFolder ? (
                              <FcFolder size={18} />
                            ) : (
                              <FcOpenedFolder size={18} />
                            )}
                          </td>
                          <td>
                            {applyClassToCharacters(folder.name, "ur")}
                          </td>
                          <td>
                            {applyClassToCharacters(
                              folder.description || "",
                              "ur"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="white" onClick={onClose} disabled={moveInProgress}>
              <GetLanguageString props="common_cancel" />
            </Button>
            <Button
              color="primary"
              disabled={isInvalidDestination || moveInProgress || loading}
              onClick={() => onMove(browseFolderId)}
            >
              <MdDriveFileMoveOutline size={16} />
              &nbsp;
              <GetLanguageString props="document_move_here" />
            </Button>
          </ModalFooter>
        </Modal>
      </Draggable>

      {newFolderModal && (
        <AddNewFolder
          setNewFolderModal={setNewFolderModal}
          parentFolderId={browseFolderId}
          uploadRefreshDocuments={refreshPicker}
          documents={folders}
          setDocuments={setFolders}
        />
      )}
    </>
  );
};

export default MoveItemsModal;
